import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React, { useContext } from 'react';

import { BillingReportContext } from '../../contexts/BillingReportProvider';
import { BillingReportRow } from '../../types';
import { isBlank } from '../../utils/isBlank';
import { isHealthieClaimId } from '../../utils/isHealthieClaimId';

/**
 * Renders the cell for the Claim ID column in the billing report grid.
 * If the CMS 1500 Healthie ID is blank, it returns the Claim Group ID.
 * Otherwise, it returns a link to the CMS 1500 Healthie ID page on Healthie. Claims
 * Homebase created have no Healthie page, so their IDs show as plain text.
 * @param data The data for the row being rendered.
 * @returns The rendered cell.
 */
const ClaimIdGridCell = ({ data }: { data?: BillingReportRow }): JSX.Element | string | null => {
  const { healthieUrl } = useContext(BillingReportContext);
  if (!data) {
    return null;
  }
  if (isBlank(data.cms_1500_healthie_id)) {
    return data.claim_group_id;
  }

  const claimIds = data.cms_1500_healthie_id.split(',');
  return (
    <>
      {claimIds.map((claimId) => {
        if (!isHealthieClaimId(claimId)) {
          return (
            <span key={claimId} className="mr-2">
              {claimId}
            </span>
          );
        }
        const url = `${healthieUrl}/cms1500s/${claimId}/edit`;
        return (
          <a
            key={claimId}
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800 mr-2">
            {claimId}
            <FontAwesomeIcon icon={faExternalLink} className="ml-1 text-xs" />
          </a>
        );
      })}
    </>
  );
};

export default ClaimIdGridCell;
